import { AlertCircle, BookOpen, ListTree, RefreshCw, RotateCcw, Sigma } from 'lucide-react';
import React, { useEffect, useRef, useState } from 'react';
import { findBookByCode, listBooks } from '../../../../services/BooksService';
import { listCompanies } from '../../../../services/CompaniesService';
import { getPurchaseOrder, listPurchaseOrders } from '../../../../services/PurchaseOrdersService';
import { buildStockAudit, listStockMovements } from '../../../../services/StockMovementsService';
import { listWarehouses } from '../../../../services/WarehousesService';
import { getActiveCompanyId } from '../../../../services/apiUtils';
import { Book, PurchaseOrder, PurchaseOrderLine, Society, Warehouse } from '../../../../types';
import { BookTypeahead, BookTypeaheadValue } from '../PurchasesView/components/BookTypeahead';
import { formatDate, formatMoney, formatQuantity } from '../PurchasesView/components/format';
import { DataTable, ColumnDef } from '../../../../components/shared/DataTable/DataTable';
import { ScreenSkeleton } from '../ScreenSkeleton';
import '../PurchasesView/PurchasesView.css';

// Mismos códigos de elemento que la lista de necesidades de SAP
type NeedElement = 'Stock' | 'RepPed' | 'OrdClt';

interface NeedRow {
  key: string;
  date: string;
  element: NeedElement;
  data: string;
  detail: string;
  quantity: number; // positiva = entrada, negativa = necesidad
  warehouseId: string;
  warehouseName: string;
  companyId: string;
}

const pendingOf = (line: PurchaseOrderLine) => Math.max(line.quantityOrdered - line.quantityReceived, 0);

const today = () => new Date().toISOString().split('T')[0];

const signed = (n: number) => (n > 0 ? `+${formatQuantity(n)}` : formatQuantity(n));

// Qué pedidos de compra y de cliente hay para un libro, con el stock por almacén
// y la cantidad disponible acumulada en el tiempo, como la lista de necesidades de
// SAP. Los pedidos de cliente no existen todavía en el backend.
export const NeedsListScreen = () => {
  const [books, setBooks] = useState<Book[]>([]);
  const [warehouses, setWarehouses] = useState<Warehouse[]>([]);
  const [companies, setCompanies] = useState<Society[]>([]);
  const [catalogsLoading, setCatalogsLoading] = useState(true);

  const [companyId, setCompanyId] = useState(() => {
    try {
      return getActiveCompanyId();
    } catch {
      return '';
    }
  });
  const [selection, setSelection] = useState<BookTypeaheadValue>({ bookId: null, query: '' });
  const [rows, setRows] = useState<NeedRow[] | null>(null);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState('');
  const [loadedAt, setLoadedAt] = useState('');

  // Las líneas solo vienen en purchase_orders_get: se guardan mientras la pantalla está abierta
  const orderCache = useRef<Record<string, PurchaseOrder>>({});

  useEffect(() => {
    let cancelled = false;
    Promise.all([
      listBooks().then(list => { if (!cancelled) setBooks(list); }),
      listWarehouses().then(list => { if (!cancelled) setWarehouses(list); }),
      listCompanies().then(list => { if (!cancelled) setCompanies(list); })
    ])
      .catch(err => {
        console.error('Error loading needs list catalogs:', err);
        if (!cancelled) setError(err instanceof Error ? err.message : 'No se pudieron cargar los datos.');
      })
      .then(() => { if (!cancelled) setCatalogsLoading(false); });
    return () => { cancelled = true; };
  }, []);

  const book = books.find(b => b.id === selection.bookId) || null;

  const loadOrder = async (id: string): Promise<PurchaseOrder> => {
    if (!orderCache.current[id]) orderCache.current[id] = await getPurchaseOrder(id);
    return orderCache.current[id];
  };

  const loadNeeds = async (target: Book) => {
    setIsLoading(true);
    setError('');
    try {
      const [movements, approved, partial] = await Promise.all([
        listStockMovements({ bookId: target.id }),
        listPurchaseOrders({ status: 'approved', pageSize: 100 }),
        listPurchaseOrders({ status: 'partially_received', pageSize: 100 })
      ]);

      const stockRows: NeedRow[] = buildStockAudit(movements.filter(m => m.bookId === target.id), books)
        .filter(r => r.currentStock !== 0)
        .map(r => ({
          key: `stock-${r.warehouseId}`,
          date: today(),
          element: 'Stock',
          data: '',
          detail: '',
          quantity: r.currentStock,
          warehouseId: r.warehouseId,
          warehouseName: r.warehouseName || warehouses.find(w => w.id === r.warehouseId)?.name || `#${r.warehouseId}`,
          companyId: warehouses.find(w => w.id === r.warehouseId)?.societyId || ''
        }));

      // Sin filtro por libro en el backend: se abre cada pedido abierto para mirar sus líneas
      const orders = await Promise.all([...approved.data, ...partial.data].map(o => loadOrder(o.id)));
      const orderRows: NeedRow[] = [];
      orders.forEach(order => {
        order.lines.forEach((line, index) => {
          if (line.bookId !== target.id || pendingOf(line) <= 0) return;
          orderRows.push({
            key: line.id,
            date: order.expectedDeliveryDate || order.issuedAt || order.createdAt,
            element: 'RepPed',
            data: `${order.poNumber}/${index + 1}`,
            detail: order.supplierName,
            quantity: pendingOf(line),
            warehouseId: order.warehouseId,
            warehouseName: order.warehouseName || `#${order.warehouseId}`,
            companyId: order.companyId
          });
        });
      });
      orderRows.sort((a, b) => a.date.localeCompare(b.date));

      setRows([...stockRows, ...orderRows]);
      setLoadedAt(new Date().toLocaleTimeString('es-ES', { hour: '2-digit', minute: '2-digit' }));
    } catch (err) {
      console.error('Error loading needs list:', err);
      setRows(null);
      setError(err instanceof Error ? err.message : 'No se pudo cargar la lista de necesidades.');
    } finally {
      setIsLoading(false);
    }
  };

  const handleSelection = (value: BookTypeaheadValue, selected?: Book) => {
    setSelection(value);
    setError('');
    if (selected) loadNeeds(selected);
    else if (!value.bookId) setRows(null);
  };

  // El lector de códigos escribe el ISBN o el código y pulsa Intro
  const handleKeyDown = (e: React.KeyboardEvent) => {
    if (e.key !== 'Enter' || selection.bookId) return;
    const found = findBookByCode(books, selection.query, 'isbn') || findBookByCode(books, selection.query, 'sku');
    if (found) handleSelection({ bookId: found.id, query: found.title }, found);
    else if (selection.query.trim()) setError(`No hay ningún libro con el código ${selection.query.trim()}.`);
  };

  const handleRefresh = () => {
    if (!book) return;
    orderCache.current = {};
    loadNeeds(book);
  };

  const handleClear = () => {
    setSelection({ bookId: null, query: '' });
    setRows(null);
    setError('');
  };

  const filteredRows = (rows || []).filter(r => !companyId || r.companyId === companyId);
  // Como en SAP, la fila de stock sale siempre, aunque sea 0
  const visibleRows: NeedRow[] = rows && !filteredRows.some(r => r.element === 'Stock')
    ? [{ key: 'stock-total', date: today(), element: 'Stock', data: '', detail: '', quantity: 0, warehouseId: '', warehouseName: '—', companyId }, ...filteredRows]
    : filteredRows;
  let running = 0;
  const rowsWithAvailable = visibleRows.map(r => {
    running += r.quantity;
    return { ...r, available: running };
  });
  const stockTotal = visibleRows.filter(r => r.element === 'Stock').reduce((acc, r) => acc + r.quantity, 0);
  const incoming = visibleRows.filter(r => r.element === 'RepPed').reduce((acc, r) => acc + r.quantity, 0);

  type NeedRowWithAvailable = NeedRow & { available: number };
  const columns: ColumnDef<NeedRowWithAvailable>[] = [
    { id: 'date', header: 'FECHA', render: (r) => formatDate(r.date) },
    { id: 'element', header: 'ELEMENTO', render: (r) => <span className={`module-badge ${r.element === 'Stock' ? 'active' : 'approved'}`}>{r.element}</span> },
    {
      id: 'data',
      header: 'DATOS DEL ELEMENTO',
      render: (r) => (
        <>
          {r.data || '—'}
          {r.detail && <div className="purchases-view__line-sub">{r.detail}</div>}
        </>
      )
    },
    { id: 'rescheduled', header: 'FE. REPROGR.', render: () => '—' },
    { id: 'exception', header: 'EXC.', render: () => '—' },
    { id: 'quantity', header: 'ENTRADA / NEC.', align: 'right', render: (r) => (r.element === 'Stock' ? formatQuantity(r.quantity) : signed(r.quantity)) },
    { id: 'available', header: 'CTD. DISPONIBLE', align: 'right', render: (r) => <span className={`module-screen-diff ${r.available < 0 ? 'negative' : ''}`}>{formatQuantity(r.available)}</span> },
    { id: 'warehouse', header: 'ALMACÉN', render: (r) => r.warehouseName }
  ];

  return (
    <div className="module-screen">
      <div className="module-screen-actions">
        <button type="button" onClick={handleRefresh} disabled={!book || isLoading} className="module-form-submit-btn">
          <RefreshCw size={12} /> {isLoading ? 'Cargando...' : 'Actualizar'}
        </button>
        <button type="button" onClick={handleClear} disabled={isLoading} className="module-form-cancel-btn">
          <RotateCcw size={12} /> Limpiar
        </button>
      </div>

      {error && (
        <div className="module-form-error">
          <AlertCircle size={14} style={{ flexShrink: 0 }} />
          <span>{error}</span>
        </div>
      )}

      {catalogsLoading ? <ScreenSkeleton blocks={2} /> : (
      <div className="module-screen-grid">
        <section className="module-panel module-screen-block">
          <h3 className="module-panel-title"><BookOpen size={14} className="module-panel-title-icon" /> Material</h3>
          <div className="module-screen-fields">
            <div className="module-form-group module-screen-span" onKeyDown={handleKeyDown}>
              <label className="module-form-label">Libro</label>
              <BookTypeahead books={books} value={selection} onChange={handleSelection} disabled={catalogsLoading || isLoading} />
            </div>
            <div className="module-form-group">
              <label className="module-form-label">Centro</label>
              <select value={companyId} onChange={(e) => setCompanyId(e.target.value)} className="module-form-select">
                <option value="">Todas las sociedades</option>
                {companies.map(c => <option key={c.id} value={c.id}>{c.name}</option>)}
              </select>
            </div>
            <div className="module-form-group">
              <label className="module-form-label">Car. plan. nec.</label>
              <input type="text" value="" placeholder="—" disabled className="module-form-input" />
            </div>
            <div className="module-form-group">
              <label className="module-form-label">Tipo material</label>
              <input type="text" value="Libro" disabled className="module-form-input" />
            </div>
            <div className="module-form-group">
              <label className="module-form-label">Unidad</label>
              <input type="text" value="EJ · Ejemplares" disabled className="module-form-input" />
            </div>
          </div>
          <p className="module-screen-hint">
            Escriba el título, o escanee el ISBN o el código y pulse Intro.
          </p>
        </section>

        <section className="module-panel module-screen-block">
          <h3 className="module-panel-title"><Sigma size={14} className="module-panel-title-icon" /> Resumen</h3>
          {book ? (
            <dl className="module-screen-data">
              <dt>Libro</dt><dd>{book.title}</dd>
              <dt>ISBN / código</dt><dd>{book.isbn || '—'}{book.sku ? ` · ${book.sku}` : ''}</dd>
              <dt>PVP</dt><dd>{formatMoney(book.price)}</dd>
              <dt>Stock</dt><dd>{formatQuantity(stockTotal)}</dd>
              <dt>Pedidos de compra</dt><dd>{signed(incoming)}</dd>
              <dt>Pedidos de cliente</dt><dd>Pendiente de endpoint</dd>
              <dt>Disponible</dt><dd><span className={`module-screen-diff ${stockTotal + incoming < 0 ? 'negative' : 'positive'}`}>{formatQuantity(stockTotal + incoming)}</span></dd>
            </dl>
          ) : (
            <p className="module-screen-hint">Elija un libro para ver su stock y sus pedidos.</p>
          )}
        </section>
      </div>
      )}

      <DataTable
        data={book ? rowsWithAvailable : []}
        columns={columns}
        title={<><ListTree size={14} className="module-panel-title-icon" /> Lista_De_Necesidades / Stocks{loadedAt ? ` de ${loadedAt} horas` : ''}</>}
        subtitle={book && !isLoading ? `TOTAL: ${rowsWithAvailable.length}` : undefined}
        isLoading={isLoading}
        getRowId={(r) => r.key}
        emptyText={book ? `Sin stock ni pedidos abiertos${companyId ? ' en esta sociedad' : ''}.` : 'Elija un libro para ver su lista de necesidades.'}
      />
      <p className="module-screen-hint">
        Stock: existencias por almacén · RepPed: pedido de compra pendiente de recibir · OrdClt: pedido de cliente (pendiente de endpoint, no se muestran).
      </p>
    </div>
  );
};
